// BattlemapsDashboard.js
// The Battlemaps control centre — the product's home page.
//
// This is NOT the Environment Pack shop. It answers three questions in order:
// what do I own, what can I make, and what have I made recently. Purchasing has
// exactly two destinations at the top (Pricing, Environment Packs) and is not
// repeated further down; the previous version restated the same "buy a pack"
// message in a hero, two large cards, an empty state and a Quick Links block,
// which made the page long without making it more useful.
//
// The lower section (Recent Builds on the left, Quick Links on the right) reuses
// the D&D / Professional lower-section LAYOUT only. Its data and every route it
// links to are Battlemaps-specific: build history is isolated in
// normalizeBattlemaps (see the domain filter) and never shared with D&D or
// Professional, and the links point only at routes that genuinely exist for
// Battlemaps. data-help-id hooks are present for a future Guided Help pass; no
// Guided Help copy is wired here (usesGuidedHelp('battlemaps') is false).
//
// Rendered only for the Battlemaps domain by Dashboard.js, which owns the props.
import React, { useMemo, useEffect, useState } from 'react';
import API from './api';
import {
  BATTLEMAPS_PACK_PRICE,
  BATTLEMAPS_PACK_INCLUDED_CREDITS,
} from './battlemapsCatalog';
import { useBattlemapsPacks } from './battlemapsCommerce';
import { BATTLEMAP_ENGINES } from './BattlemapsEngines';
import { glassSurface } from './config/glassTheme';

// ---- user-facing group labels (singular) for activity rows ----
const GROUP_SINGULAR = {
  'Tactical Battlemaps': 'Tactical Battlemap',
  'Terrain Tiles': 'Terrain Tile',
  'Structure Assets': 'Structure Asset',
  'Props & Objects': 'Prop & Object',
  'Cinematic Scenes': 'Cinematic Scene',
};

// ---- single Battlemaps data-normalisation layer ----
// Consumes whatever the current/future backend supplies and yields a stable shape.
// Safe fallbacks: no packs, 0 credits, no history. No D&D/Professional data leaks in.
function normalizeBattlemaps(usage, recentJobs, animCredits, packsById) {
  // packsById is the PUBLISHED catalogue, not a constant compiled into the
  // bundle: a pack published by the pipeline names itself here without anyone
  // editing this file. The minimal fallback below is unchanged and still covers
  // a pack the catalogue has not caught up with.
  const bm = (usage && usage.battlemaps) || {};

  let ownedRaw = bm.owned_packs
    || (usage && usage.owned_battlemap_packs)
    || (usage && usage.battlemap_pack_ownership)
    || [];
  if (!Array.isArray(ownedRaw)) ownedRaw = [];
  const ownedPacks = ownedRaw
    .map((p) => {
      const slug = typeof p === 'string' ? p : (p.slug || p.pack_slug || p.id);
      if (!slug) return null;
      const cat = (packsById || {})[slug];
      if (cat) return cat;
      // pack not in current catalogue module: render minimally, never fabricate generators
      return typeof p === 'object'
        ? { id: slug, name: p.name || p.pack_name || slug, categoryName: p.categoryName || p.category || '', groups: Array.isArray(p.groups) ? p.groups : [], price: BATTLEMAPS_PACK_PRICE, includedCredits: BATTLEMAPS_PACK_INCLUDED_CREDITS }
        : null;
    })
    .filter(Boolean);

  // Creation Credits — the Battlemaps generation currency. `generations` is the
  // superseded key name, still read so an older payload does not show zero.
  const creationCredits = Number(
    bm.creation_credits != null ? bm.creation_credits
      : (bm.generations != null ? bm.generations
        : (usage && usage.battlemap_generations != null ? usage.battlemap_generations : 0))
  ) || 0;

  let histRaw = bm.history
    || bm.recent_jobs
    || (usage && usage.battlemap_history)
    || (usage && usage.battlemaps_recent_jobs)
    || null;
  if (!Array.isArray(histRaw)) {
    histRaw = Array.isArray(recentJobs)
      ? recentJobs.filter((j) => j && String(j.domain || '').toLowerCase() === 'battlemaps')
      : [];
  }
  const battlemapHistory = histRaw
    .map((j) => {
      if (!j) return null;
      const packSlug = j.pack_slug || j.pack || null;
      const pack = packSlug ? (packsById || {})[packSlug] : null;
      const groupName = j.group_name || j.group || '';
      return {
        id: j.id || j.job_id || null,
        title: j.title || j.name || j.output_name || 'Battlemaps Build',
        packName: (pack && pack.name) || j.pack_name || '',
        group: GROUP_SINGULAR[groupName] || groupName || '',
        thumb: (j.result && j.result.url) || j.thumb_url || j.thumbnail || null,
        date: j.updated_at || j.created_at || j.date || null,
      };
    })
    .filter(Boolean);

  return {
    ownedPacks,
    ownedPackCount: ownedPacks.length,
    creationCredits,
    battlemapHistory,
    animationCredits: Number(animCredits != null ? animCredits : 0) || 0,
  };
}

function timeAgo(v) {
  if (!v) return '';
  const d = new Date(v);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
}

// Quick Links — every destination is a route that genuinely exists for
// Battlemaps (see src/App.js). Nothing here is invented. "Change Password"
// opens the existing dashboard modal via the openPasswordModal prop rather than
// navigating. Each row carries a stable data-help-id for a future Guided Help
// pass; no help copy is attached in this pass.
const BM_QUICK_LINKS = [
  { helpId: 'bm-ql-packs',     icon: '🧩', title: 'Environment Packs', sub: 'Own an environment across every system', route: '/battlemaps/packs' },
  { helpId: 'bm-ql-library',   icon: '🗺️', title: 'Map Libraries',     sub: 'Browse tactical maps, terrain and props', route: '/generators?library=battlemaps' },
  { helpId: 'bm-ql-engines',   icon: '🛠️', title: 'Creation Systems',  sub: 'Open the five Battlemaps build engines', route: '/battlemaps/engines' },
  { helpId: 'bm-ql-workspace', icon: '📋', title: 'My Builds',         sub: 'Open your saved Battlemaps workspace',   route: '/workspace' },
  { helpId: 'bm-ql-pricing',   icon: '💳', title: 'Pricing & Plans',   sub: 'Credits, packs and subscription options', route: '/battlemaps/pricing' },
  { helpId: 'bm-ql-rewards',   icon: '🎁', title: 'Rewards & Referrals', sub: 'Earn credits by sharing VISINVOKE',    route: '/rewards' },
  { helpId: 'bm-ql-account',   icon: '⚙️', title: 'Account & Billing', sub: 'Manage your subscription and details',   route: '/manage' },
  { helpId: 'bm-ql-password',  icon: '🔒', title: 'Change Password',   sub: 'Update your account password',           passwordModal: true },
];

export default function BattlemapsDashboard(props) {
  const { usage, recentJobs, jobsLoading, animCredits, loading, navigate, openPasswordModal } = props;

  // Owned packs, Creation Credit balance and Battlemaps-only history come from
  // the dedicated /v1/battlemaps/account endpoint — /v1/usage/current does not
  // carry them.
  const [bmAccount, setBmAccount] = useState(null);
  useEffect(() => {
    let active = true;
    API.get('/v1/battlemaps/account')
      .then((r) => { if (active) setBmAccount((r.data && r.data.battlemaps) || null); })
      .catch(() => { /* not live yet / not authed: safe empty fallback renders */ });
    return () => { active = false; };
  }, []);

  // The published Environment Pack catalogue, so an owned pack's name and
  // contents render without this file carrying a copy of the catalogue.
  const { packsById } = useBattlemapsPacks();

  const data = useMemo(
    () => normalizeBattlemaps(
      bmAccount ? { ...(usage || {}), battlemaps: bmAccount } : usage,
      recentJobs,
      animCredits,
      packsById,
    ),
    [bmAccount, usage, recentJobs, animCredits, packsById],
  );

  if (loading) {
    return <div style={bs.loading}>Loading your Battlemaps account…</div>;
  }

  const { ownedPacks, ownedPackCount, creationCredits, battlemapHistory } = data;
  const hasPacks = ownedPackCount > 0;
  const hasHistory = battlemapHistory.length > 0;

  const onQuickLink = (link) => {
    if (link.passwordModal) {
      if (typeof openPasswordModal === 'function') openPasswordModal();
      return;
    }
    navigate(link.route);
  };

  return (
    <div style={bs.root}>

      {/* ── Identity + the only two commercial destinations on the page ── */}
      <section style={bs.header}>
        <div style={bs.headerText} data-help-id="bm-control-centre">
          <div style={bs.eyebrow}>Battlemaps</div>
          <h2 style={bs.headerTitle}>Your Battlemaps control centre</h2>
          <p style={bs.headerBody}>
            Five creation systems, one shared environment library. Buy an environment
            once and it unlocks across every system, permanently.
          </p>
        </div>
        <div style={bs.headerActions}>
          <button type="button" style={bs.btnPrimary} data-help-id="bm-header-packs" onClick={() => navigate('/battlemaps/packs')}>
            Environment Packs
          </button>
          <button type="button" style={bs.btnSecondary} data-help-id="bm-header-pricing" onClick={() => navigate('/battlemaps/pricing')}>
            Pricing
          </button>
        </div>
      </section>

      {/* ── Live account state ── */}
      <section style={bs.statRow}>
        <div style={bs.statCard} data-help-id="bm-packs-owned">
          <div style={bs.statLabel}>Environment Packs owned</div>
          <div style={bs.statValue}>{ownedPackCount}</div>
          <div style={bs.statHelp}>
            {hasPacks
              ? 'Unlocked across all five creation systems'
              : 'No packs yet — every pack unlocks one environment everywhere'}
          </div>
        </div>
        <div style={bs.statCard} data-help-id="bm-creation-credits">
          <div style={bs.statLabel}>Creation Credits</div>
          <div style={bs.statValue}>{creationCredits}</div>
          <div style={bs.statHelp}>
            Spent when you generate. Cinematic video costs more than a still map.
          </div>
        </div>
        <div style={bs.statCard} data-help-id="bm-recent-metric">
          <div style={bs.statLabel}>Recent builds</div>
          <div style={bs.statValue}>{jobsLoading ? '—' : battlemapHistory.length}</div>
          <div style={bs.statHelp}>
            {hasHistory ? `Last build ${timeAgo(battlemapHistory[0].date) || 'recently'}` : 'Nothing generated yet'}
          </div>
        </div>
      </section>

      {/* ── Creation suite: what you can make. No purchase buttons in here. ── */}
      <section style={bs.section}>
        <div style={bs.sectionHead}>
          <h3 style={bs.sectionTitle} data-help-id="bm-creation-suite">Creation Suite</h3>
          <span style={bs.sectionHint}>{BATTLEMAP_ENGINES.length} systems</span>
        </div>
        <div style={bs.engineGrid}>
          {BATTLEMAP_ENGINES.map((e) => (
            <article key={e.key} style={bs.engineCard} data-help-id={`bm-engine-${e.key}`}>
              <div style={bs.engineTop}>
                <span style={{ ...bs.engineIcon, background: e.gradient }}>{e.icon}</span>
                {e.video ? <span style={bs.videoBadge} data-help-id="bm-video-badge">Video</span> : <span style={bs.engineBadgeSpacer} />}
              </div>
              <div style={bs.engineName}>{e.name}</div>
              <p style={bs.engineDesc}>{e.desc}</p>
              <button
                type="button"
                style={{ ...bs.engineBtn, ...glassSurface(e.color, e.color, e.color) }}
                data-help-id={`bm-engine-${e.key}-open`}
                onClick={() => navigate(e.route)}
              >
                Open {e.name}
              </button>
            </article>
          ))}
        </div>
      </section>

      {/* ── Owned packs: only rendered when there is something to show ── */}
      {hasPacks && (
        <section style={bs.section}>
          <div style={bs.sectionHead}>
            <h3 style={bs.sectionTitle} data-help-id="bm-owned-packs">Your Environment Packs</h3>
            <button type="button" style={bs.linkBtn} data-help-id="bm-packs-browse" onClick={() => navigate('/battlemaps/packs')}>
              Browse all
            </button>
          </div>
          <div style={bs.packGrid}>
            {ownedPacks.map((p) => (
              <div key={p.id} style={bs.packCard}>
                <div style={bs.packCat}>{p.categoryName || 'Environment'}</div>
                <div style={bs.packName}>{p.name}</div>
                <div style={bs.packOwned}>Owned</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ── Lower section: Recent Builds (left, large) + Quick Links (right).
           Mirrors the D&D / Professional lower layout (2fr : 1fr). Data and
           routes are Battlemaps-only; build history is never shared with D&D or
           Professional (see normalizeBattlemaps). ── */}
      <section style={bs.lowerGrid}>

        {/* LEFT — Battlemaps Recent Builds */}
        <div style={bs.buildCard} data-help-id="bm-recent-card">
          <div style={bs.buildCardTop}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={bs.buildCardTitle}>Recent Builds</div>
              <div style={bs.buildCardDesc}>
                Your latest Battlemaps generations. Open the workspace to keep building.
              </div>
            </div>
            <button type="button" style={bs.buildOpenBtn} data-help-id="bm-workspace" onClick={() => navigate('/workspace')}>
              Open Workspace
            </button>
          </div>

          {jobsLoading ? (
            <div style={bs.recentBuildsEmpty}>Loading…</div>
          ) : !hasHistory ? (
            <div style={bs.emptyBuilds} data-help-id="bm-recent-empty">
              <div style={bs.emptyBuildsIcon}>🗺️</div>
              <div style={bs.emptyBuildsTitle}>No battlemaps generated yet</div>
              <div style={bs.emptyBuildsBody}>
                Open a creation system above to generate your first map. Completed builds appear here.
              </div>
            </div>
          ) : (
            <div style={bs.buildGrid}>
              {battlemapHistory.slice(0, 6).map((h, i) => (
                <article key={h.id || i} style={bs.buildItem}>
                  <div style={bs.buildThumbWrap}>
                    {h.thumb
                      ? <img src={h.thumb} alt="" style={bs.buildImg} data-help-id={i === 0 ? 'bm-build-image' : undefined} />
                      : <div style={bs.buildThumbFallback}>🗺️</div>}
                  </div>
                  <div style={bs.buildBody}>
                    <div style={bs.buildName} data-help-id={i === 0 ? 'bm-build-name' : undefined}>{h.title}</div>
                    <div style={bs.buildMeta}>
                      {[h.group, h.packName].filter(Boolean).join(' · ') || 'Battlemaps'}
                    </div>
                    <div style={bs.buildDate} data-help-id={i === 0 ? 'bm-build-date' : undefined}>{timeAgo(h.date)}</div>
                    <button type="button" style={bs.buildItemBtn} data-help-id={i === 0 ? 'bm-build-open' : undefined} onClick={() => navigate('/workspace')}>
                      Open in Workspace
                    </button>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>

        {/* RIGHT — Quick Links (real Battlemaps routes only) */}
        <div style={bs.quickCard} data-help-id="bm-quicklinks-card">
          <div style={bs.quickCardLabel}>Quick Links</div>
          <div style={bs.quickList}>
            {BM_QUICK_LINKS.map((link) => (
              <button
                key={link.helpId}
                type="button"
                style={bs.quickRow}
                data-help-id={link.helpId}
                onClick={() => onQuickLink(link)}
              >
                <span style={bs.quickRowIcon}>{link.icon}</span>
                <span style={bs.quickRowText}>
                  <span style={bs.quickRowTitle}>{link.title}</span>
                  <span style={bs.quickRowSub}>{link.sub}</span>
                </span>
              </button>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Styles.
//
// Card regions are fixed-height where content length varies (label, value,
// helper, name, description) so equivalent cards sitting side by side align
// internally rather than merely sharing an outer height. Actions use
// marginTop:auto so buttons land on the same line across a row.
// ---------------------------------------------------------------------------
const AMBER = '#F59A23';
const CARD_BG = 'linear-gradient(158deg, rgba(245,154,35,0.07) 0%, rgba(245,154,35,0.02) 60%, rgba(255,255,255,0.012) 100%), var(--vi-s1, #281B0D)';
const CARD_BORDER = '1px solid rgba(245,154,35,0.22)';
const CARD_SHADOW = '0 10px 28px rgba(0,0,0,0.28)';

const bs = {
  root: { display: 'flex', flexDirection: 'column', gap: 20, width: '100%' },
  loading: { background: CARD_BG, border: CARD_BORDER, borderRadius: 16, padding: 40, color: 'rgba(255,255,255,0.7)', textAlign: 'center' },

  btnPrimary: { ...glassSurface('#A85B0A','#F59A23','#FFB33F'), borderRadius: 999, padding: '12px 26px', fontWeight: 800, fontSize: 14, cursor: 'pointer', whiteSpace: 'nowrap' },
  btnSecondary: { ...glassSurface('#A85B0A','#F59A23','#FFB33F'), borderRadius: 999, padding: '12px 26px', fontWeight: 700, fontSize: 14, cursor: 'pointer', whiteSpace: 'nowrap' },
  linkBtn: { background: 'transparent', border: 'none', color: AMBER, fontWeight: 700, fontSize: 13, cursor: 'pointer', textDecoration: 'underline', padding: 0 },

  header: { background: CARD_BG, border: CARD_BORDER, borderRadius: 16, boxShadow: CARD_SHADOW, padding: 28, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 24, flexWrap: 'wrap' },
  headerText: { flex: 1, minWidth: 280, display: 'flex', flexDirection: 'column', gap: 8 },
  eyebrow: { fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: AMBER },
  headerTitle: { margin: 0, fontWeight: 800, fontSize: 'clamp(22px, 2.2vw, 30px)', color: '#f4f4f8', lineHeight: 1.15 },
  headerBody: { margin: 0, fontSize: 'clamp(14px, 1.1vw, 16px)', color: 'rgba(255,255,255,0.66)', lineHeight: 1.6, maxWidth: 640 },
  headerActions: { display: 'flex', gap: 12, flexShrink: 0, flexWrap: 'wrap' },

  statRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16, alignItems: 'stretch' },
  statCard: { background: CARD_BG, border: CARD_BORDER, borderRadius: 14, padding: 22, display: 'flex', flexDirection: 'column', boxSizing: 'border-box', height: '100%' },
  statLabel: { fontSize: 12, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: AMBER, height: 16 },
  statValue: { fontSize: 'clamp(30px, 3vw, 40px)', fontWeight: 900, lineHeight: 1.1, color: '#f4f4f8', margin: '10px 0 8px', height: 44, display: 'flex', alignItems: 'center' },
  statHelp: { fontSize: 13, color: 'rgba(255,255,255,0.55)', lineHeight: 1.5, marginTop: 'auto' },

  section: { background: CARD_BG, border: CARD_BORDER, borderRadius: 16, boxShadow: CARD_SHADOW, padding: 28, display: 'flex', flexDirection: 'column', gap: 16, boxSizing: 'border-box' },
  sectionHead: { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 },
  sectionTitle: { margin: 0, fontWeight: 800, fontSize: 'clamp(18px, 1.7vw, 22px)', color: '#f4f4f8' },
  sectionHint: { fontSize: 13, color: 'rgba(255,255,255,0.45)' },

  engineGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 16, alignItems: 'stretch' },
  engineCard: { background: 'rgba(255,255,255,0.028)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 12, padding: 18, display: 'flex', flexDirection: 'column', boxSizing: 'border-box', height: '100%' },
  engineTop: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 40 },
  engineIcon: { width: 38, height: 38, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 19, flexShrink: 0 },
  videoBadge: { ...glassSurface('#A85B0A','#F59A23','#FFB33F'), fontSize: 10, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', borderRadius: 999, padding: '3px 9px' },
  engineBadgeSpacer: { display: 'block', width: 1, height: 1 },
  engineName: { fontWeight: 800, fontSize: 15.5, color: '#f4f4f8', marginTop: 12, minHeight: 40, lineHeight: 1.3 },
  engineDesc: { margin: '2px 0 0', fontSize: 13, color: 'rgba(255,255,255,0.58)', lineHeight: 1.55, minHeight: 82 },
  engineBtn: { marginTop: 'auto', background: 'transparent', border: '1.5px solid', borderRadius: 999, padding: '10px 0', fontWeight: 700, fontSize: 13, width: '100%', cursor: 'pointer' },

  packGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 14, alignItems: 'stretch' },
  packCard: { background: 'rgba(245,154,35,0.05)', border: '1px solid rgba(245,154,35,0.2)', borderRadius: 12, padding: 16, display: 'flex', flexDirection: 'column', height: '100%', boxSizing: 'border-box' },
  packCat: { fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.45)', height: 15 },
  packName: { fontWeight: 800, fontSize: 15, color: '#f4f4f8', marginTop: 6, minHeight: 40, lineHeight: 1.3 },
  packOwned: { marginTop: 'auto', fontSize: 11, fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#7bd88f' },

  // ── Lower section: Recent Builds (left, 2fr) + Quick Links (right, 1fr) ──
  // Same proportions as the D&D / Professional lower grid (see s.bottomGrid).
  lowerGrid: { display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 20, alignItems: 'stretch' },

  buildCard: { background: CARD_BG, border: CARD_BORDER, borderRadius: 16, boxShadow: CARD_SHADOW, padding: 28, display: 'flex', flexDirection: 'column', gap: 18, boxSizing: 'border-box' },
  buildCardTop: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 },
  buildCardTitle: { fontWeight: 800, fontSize: 'clamp(18px, 1.7vw, 22px)', color: '#f4f4f8' },
  buildCardDesc: { fontSize: 13, color: 'rgba(255,255,255,0.6)', lineHeight: 1.5, marginTop: 5, maxWidth: 520 },
  buildOpenBtn: { ...glassSurface('#A85B0A','#F59A23','#FFB33F'), flexShrink: 0, borderRadius: 8, padding: '9px 18px', fontWeight: 800, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' },

  buildGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16, alignItems: 'stretch' },
  buildItem: { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)', borderRadius: 12, overflow: 'hidden', display: 'flex', flexDirection: 'column', boxSizing: 'border-box' },
  buildThumbWrap: { height: 150, overflow: 'hidden', flexShrink: 0, background: 'rgba(245,154,35,0.06)' },
  buildImg: { width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center top', display: 'block' },
  buildThumbFallback: { width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 34 },
  buildBody: { padding: '12px 14px 14px', display: 'flex', flexDirection: 'column', gap: 4, flex: 1 },
  buildName: { fontWeight: 700, fontSize: 14, color: '#f4f4f8', lineHeight: 1.35, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' },
  buildMeta: { fontSize: 12, color: 'rgba(255,255,255,0.5)' },
  buildDate: { fontSize: 11.5, color: 'rgba(255,255,255,0.4)', fontStyle: 'italic' },
  buildItemBtn: { ...glassSurface('#A85B0A','#F59A23','#FFB33F'), marginTop: 'auto', borderRadius: 999, padding: '8px 0', fontWeight: 700, fontSize: 12, cursor: 'pointer', width: '100%' },

  emptyBuilds: { border: '1px dashed rgba(245,154,35,0.3)', borderRadius: 12, padding: '34px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 8, background: 'rgba(245,154,35,0.03)' },
  emptyBuildsIcon: { fontSize: 34, opacity: 0.9 },
  emptyBuildsTitle: { fontWeight: 800, fontSize: 16, color: '#f4f4f8' },
  emptyBuildsBody: { fontSize: 13, color: 'rgba(255,255,255,0.6)', lineHeight: 1.55, maxWidth: 420 },
  recentBuildsEmpty: { fontSize: 13, color: 'rgba(255,255,255,0.4)', fontStyle: 'italic', padding: '10px 0' },

  quickCard: { background: CARD_BG, border: CARD_BORDER, borderRadius: 16, boxShadow: CARD_SHADOW, padding: 24, display: 'flex', flexDirection: 'column', gap: 14, boxSizing: 'border-box' },
  quickCardLabel: { fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', color: AMBER },
  quickList: { display: 'flex', flexDirection: 'column', gap: 8 },
  quickRow: { ...glassSurface('#A85B0A','#F59A23','#FFB33F'), boxShadow: 'inset 0 0 22px rgba(245,154,35,0.20), inset 0 1px 0 rgba(255,255,255,0.22), 0 6px 16px rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', gap: 13, borderRadius: 10, padding: '12px 15px', cursor: 'pointer', textAlign: 'left', width: '100%', boxSizing: 'border-box' },
  quickRowIcon: { fontSize: 21, flexShrink: 0, lineHeight: 1 },
  quickRowText: { display: 'flex', flexDirection: 'column', minWidth: 0 },
  quickRowTitle: { fontWeight: 700, fontSize: 14, color: '#eef0f5' },
  quickRowSub: { fontSize: 12, color: 'rgba(255,255,255,0.45)', marginTop: 2 },
};
